import React, { useEffect, useReducer, useState } from "react";
import Context from "../context";
import styled, { ThemeProvider, createGlobalStyle } from "styled-components";
import { Helmet } from "react-helmet";
import { graphql, useStaticQuery } from "gatsby";
import "bootstrap/dist/css/bootstrap.min.css";
import { formatData } from "../util";
import { WhatsAppOutlined, PhoneOutlined, LoadingOutlined, CloseOutlined } from "@ant-design/icons";

import MobileHeader from "./mobile-header";
import DesktopHeader from "./desktop-header";
import Footer from "./footer";

// ✅ popup image (webpack)
import popupImg from "../images/popup.png";

const GlobalStyles = createGlobalStyle`
  *{
    font-family: 'Poppins', 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
  }
  html{
    font-size: 14px;
    scroll-behavior: smooth;
    -webkit-font-smoothing: antialiased;
    @media(min-width: 768px){
      font-size: 16px;
    }
    @media(min-width: 1200px){
      font-size: 18px;
    }    
  }
  body{
    color: #333333;
    background-color: #f2f0eb;
    margin: 0;
  }
  a{
    text-decoration: none !important;
    &:visited{
      color: hsl(0, 0%, 23%);
    }
  }
  h1, h2, h3, h4, h5, h6{
    font-family: 'Poppins', 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
    font-weight: 700;
  }
  button, input, select, option, textarea{
    outline: none;
    font-family: 'Poppins', 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
  }
  input, select, option, textarea{
    padding-left: .7rem !important;
  }
  .tl-edges {
    width: 100% !important;
  }
  .image-gallery-slide-wrapper{
    @media(min-width:768px){
      width: calc(100% - 80px) !important;
    }
  }
  .image-gallery-thumbnails-wrapper{
    @media(min-width:768px){
      width: 70px !important;
    }    
  }
  .thumbnail-custom{
    @media(min-width:768px){
      width: 70px !important;
    }    
    img{
      max-height: 70px;
    }
  }
`;

const MainCont = styled.div`
  padding-top: 0;
  overflow-x: visible !important;
`;

const LoadingCont = styled.div`
  min-height: 100vh;
  background-color: #00474B;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 2rem;  
  color: #ffffff;
`;

const ContactButton = styled.a`
  display: none;
  @media(min-width: 768px){
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: ${props => props.phone ? "50%" : "calc(50% + 55px + 1rem)"};
    right: 1rem;
    background-color: #fff;
    color: #212121;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    transition: 250ms ease;
    font-size: 2rem;
    border: 1px solid rgba(0, 0, 0, .12);
    z-index: 1000;
    box-shadow: 0px 1px 1px rgba(0, 0, 0, .12),
                0px 2px 2px rgba(0, 0, 0, .12),
                0px 4px 4px rgba(0, 0, 0, .12),
                0px 8px 8px rgba(0, 0, 0, .12);
    &:hover{
      color: ${props => props.phone ? props.theme.primaryColor : "#06d755"};
    }
  }
`;

/* ✅ Popup styles */
const PopupOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 20000;
`;

const PopupBox = styled.div`
  position: relative;
  width: min(920px, 92vw);
  border-radius: 14px;
  overflow: hidden;

  /* ✅ transparente */
  background: transparent;
  box-shadow: none;
`;

const PopupClose = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  border: none;
  background: rgba(0,0,0,.55);
  color: #fff;
  width: 38px;
  height: 38px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  cursor: pointer;
  z-index: 2;
`;

const PopupImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

export default ({ children, location }) => {
  const [data, setData] = useReducer((current, next) => ({ ...current, ...next }), {
    loading: true,
    error: false,
    data: null,
  });

  // ✅ Popup state
  const [showPopup, setShowPopup] = useState(false);

  const runtimeData = useStaticQuery(graphql`
    query {
      initial {
        data
      }
    }
  `);

  const getFeatured = async (id, typeId, maxProperties) => {
    try {
      const res = await fetch(
        `https://wsnzm.clasihome.com:3443/api/conv/properties?email=propiedadeslasflores@gmail.com&id=${id}&typeId=${typeId}&status=PUBLICADA&limit=${maxProperties}`
      );
      const result = await res.json();
      return result;
    } catch (e) {
      console.error("ERROR PROPIEDADES DESTACADAS", e);
    }
  };

  const handleData = async () => {
    const preview = /builderId/.test(location.search);
    const builderId = preview && location.search.match("[?&]" + "builderId" + "=([^&]+)")[1];
    try {
      if (runtimeData.initial.data) {
        const dataEval = formatData(JSON.parse(runtimeData.initial.data));
        const featuredProperties = await getFeatured(
          dataEval.officeId,
          dataEval.typeId,
          dataEval.home.properties.maxProperties
        );
        dataEval.featuredProperties = featuredProperties && featuredProperties.properties ? featuredProperties.properties : [];
        setData({ loading: false, data: dataEval });
      } else if (builderId) {
        const data = await fetch(`https://api.clasihome.com/rest/builders?builderId=${builderId}`);
        const result = await data.json();
        const dataEval = formatData(result);
        const featuredProperties = await getFeatured(
          dataEval.officeId,
          dataEval.typeId,
          dataEval.home.properties.maxProperties
        );
        dataEval.featuredProperties = featuredProperties && featuredProperties.properties ? featuredProperties.properties : [];
        setData({ loading: false, data: dataEval });
      } else {
        const dataEval = formatData({ office: "5e8e36b31c9d440000d35090" });
        const featuredProperties = await getFeatured(
          dataEval.officeId,
          dataEval.typeId,
          dataEval.home.properties.maxProperties
        );
        dataEval.featuredProperties = featuredProperties && featuredProperties.properties ? featuredProperties.properties : [];
        setData({ loading: false, data: dataEval });
      }
    } catch (e) {
      setData({ loading: false, error: e });
    }
  };

  useEffect(() => {
    handleData();
  }, []);

  // Cerrar el aviso promocional con ESC cuando se active manualmente.
  useEffect(() => {
    if (!showPopup) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowPopup(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showPopup]);

  if (data.loading)
    return (
      <LoadingCont>
        <LoadingOutlined spin />
      </LoadingCont>
    );

  if (data.error)
    return <LoadingCont>Error de conexion</LoadingCont>;

  return (
    <Context.Provider value={data.data}>
      <ThemeProvider theme={data.data}>
        <MainCont>
          <Helmet>
            <meta charSet="utf-8" />
            <meta
              name="description"
              content="Compra, venta y arriendo de propiedades, tasaciones y desarrollo de proyectos en la región metropolitana, la quinta región y el sur de Chile."
            />
            <meta
              name="keywords"
              content="Las Flores Propiedades, propiedades Chile, corretaje, venta, arriendo, tasaciones, arquitectura, construcción"
            />
            <title>Las Flores Propiedades - Asesoría inmobiliaria</title>
            <link rel="canonical" href="https://lasflorespropiedades.cl/" />
            <meta property="og:url" content="https://lasflorespropiedades.cl/" />
            <meta property="og:title" content="Las Flores Propiedades - Asesoría inmobiliaria" />
            <meta
              property="og:description"
              content="Asesoría inmobiliaria integral y desarrollo de proyectos en la región metropolitana, la quinta región y el sur de Chile."
            />
            <meta property="og:image" content="https://lasflorespropiedades.cl/logotipo_full.png" />
            <meta name="og:type" content="website" />
            <meta name="theme-color" content={data.data.primaryColor} />
            <link rel="icon" href={data.data.favicon} />
          </Helmet>

          <GlobalStyles />

          {/* ✅ POPUP solo en Home */}
          {showPopup && (
            <PopupOverlay onClick={() => setShowPopup(false)}>
              <PopupBox onClick={(e) => e.stopPropagation()}>
                <PopupClose aria-label="Cerrar" onClick={() => setShowPopup(false)}>
                  <CloseOutlined />
                </PopupClose>
                <PopupImage src={popupImg} alt="Popup" />
              </PopupBox>
            </PopupOverlay>
          )}

          <MobileHeader />
          <DesktopHeader />
          {children}

          <ContactButton
            title="Enviar WhatsApp"
            rel="noopener"
            target="_blank"
            href={`https://api.whatsapp.com/send?phone=${data.data.movil.replace(
              /\s/g,
              ""
            )}&text=Hola,%20estoy%20visitando%20su%20sitio%20Web%20y%20quisiera%20comunicarme%20con%20uestedes.`}
          >
            <WhatsAppOutlined />
          </ContactButton>

          <ContactButton title="Llamar" phone href={`tel:${data.data.phone.replace(/\s/g, "")}`}>
            <PhoneOutlined />
          </ContactButton>

          <Footer />
        </MainCont>
      </ThemeProvider>
    </Context.Provider>
  );
};
